import React, { useCallback } from 'react';

import PropTypes from 'prop-types';

import { useFeatures } from '@atc/features';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader, ModalScrollPanel, ModalTitle } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { CertifiedProgramOffer } from 'reaxl-molecules';

import { cpoFilterTileClick } from '@/analytics/srpAnalyticsHandlers';

import useSrpNavigation from '@/hooks/useSrpNavigation';

function CertifiedPreOwnedModal({
    showModal = false,
    handleCloseModal = () => {},
    data = {},
    filtersValues = {},
}) {

    const {
        carbravo_entry_points: [enableCarBravoEntryPoints],
        cpo_programs: [, { gm_makes: GM_MAKES }],
        disable_cpo_modal_footer: [disableModalFooter],
    } = useFeatures([
        'carbravo_entry_points',
        'cpo_programs',
        'disable_cpo_modal_footer',
    ]);

    const carBravoMakes = Object.keys(GM_MAKES);
    const isCarBravoMake = carBravoMakes.includes(data?.makeCode?.toUpperCase());
    const showCarBravoCTA = enableCarBravoEntryPoints && isCarBravoMake;
    const showModalFooter = !(isCarBravoMake && disableModalFooter);

    const cpoButtonText = showCarBravoCTA ? 'Shop CarBravo Certified Vehicles' : 'Include Certified Vehicles';

    const { makeLabel, makeCode, certifiedData = {} } = data;

    const navigateToSrp = useSrpNavigation();

    const handleCertifiedModalSearchClick = useCallback((event) => {
        const updatedFilterValues = {
            ...filtersValues,
        };

        if (showCarBravoCTA) {
            updatedFilterValues.experience = 'car-bravo';
            updatedFilterValues.carProduct = 'CARBRAVO_CERT';
        }

        sendClick(cpoFilterTileClick, event, {
            cmp: 'srch_cert_v',
            makeCode,
        });

        // Add certified to existing filterValues
        if (!showCarBravoCTA && Array.isArray(updatedFilterValues.listingType) && !updatedFilterValues.listingType.includes('CERTIFIED')) {
            updatedFilterValues.listingType.push('CERTIFIED');
        } else {
            updatedFilterValues.listingType = ['CERTIFIED'];
        }

        navigateToSrp({
            resetPagination: true,
            isNewSearch: true,
            filtersValues: updatedFilterValues,
            action: 'replace',
        });

        handleCloseModal();

    }, [
        filtersValues,
        handleCloseModal,
        makeCode,
        navigateToSrp,
        showCarBravoCTA,
    ]);

    const CertifiedProgramOfferData = {
        ...certifiedData,
        content: certifiedData.combineContent || certifiedData.content,
        heading: certifiedData.combineHeading || certifiedData.heading,
    };

    return (
        <Modal
            data-cmp="modal-cert-dtls"
            show={showModal}
            onHide={handleCloseModal}
            dialogClassName="certified-tile-modal"
        >
            <ModalHeader closeButton>
                <ModalTitle>
                    {`${makeLabel} Certified ${makeLabel === 'Audi' ? 'pre-owned ' : ''}Program Details`}
                </ModalTitle>
            </ModalHeader>
            <ModalBody>
                <ModalScrollPanel>
                    <CertifiedProgramOffer
                        data={CertifiedProgramOfferData}
                        showHeading={false}
                        showTile={false}
                        showCtaLinks={false}
                        className="margin-right-2"
                    />
                </ModalScrollPanel>
            </ModalBody>
            {showModalFooter && (
                <ModalFooter data-cmp="cpo-modal-footer">
                    <Button
                        bsStyle="link"
                        onClick={handleCloseModal}
                    >
                        Cancel
                    </Button>
                    <Button
                        bsStyle="primary"
                        onClick={handleCertifiedModalSearchClick}
                    >
                        {cpoButtonText}
                    </Button>
                </ModalFooter>
            )}
        </Modal>
    );
}

export default CertifiedPreOwnedModal;

CertifiedPreOwnedModal.propTypes = {
    showModal: PropTypes.bool,
    handleCloseModal: PropTypes.func,
    data: PropTypes.shape({
        makeLabel: PropTypes.string,
        makeCode: PropTypes.string,
        certifiedData: PropTypes.shape({
            combineContent: PropTypes.string,
            content: PropTypes.string,
            combineHeading: PropTypes.string,
            heading: PropTypes.string,
        }),
    }),
    filtersValues: PropTypes.shape({
        makeCode: PropTypes.arrayOf(PropTypes.string),
        listingType: PropTypes.arrayOf(PropTypes.string),
    }),
};

CertifiedPreOwnedModal.defaultProps = {
    showModal: false,
    handleCloseModal: () => {},
    data: {},
    filtersValues: {},
};
